"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Bitmovin = void 0;
const bitmovin_player_1 = require("bitmovin-player");
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const AbstractBaseEngine_1 = require("./AbstractBaseEngine");
const interfaces_1 = require("./interfaces");
function createAudioTrack(track, id) {
    var _a;
    const roles = (_a = track.role) === null || _a === void 0 ? void 0 : _a.map((el) => el.value).filter((el) => el !== undefined);
    const kind = (0, AbstractBaseEngine_1.getAudioKind)(roles !== null && roles !== void 0 ? roles : []);
    return {
        id,
        language: track.lang || "",
        label: track.label || (0, js_player_shared_1.getLabel)(track.lang),
        kind,
        raw: track,
    };
}
function createSubtitleTrack(track, id) {
    var _a;
    const roles = (_a = track.role) === null || _a === void 0 ? void 0 : _a.map((el) => el.value).filter((el) => el !== undefined);
    const kind = (0, AbstractBaseEngine_1.getTextKind)(roles !== null && roles !== void 0 ? roles : []);
    return {
        id,
        language: track.lang || "",
        label: track.label || (0, js_player_shared_1.getLabel)(track.lang),
        kind,
        raw: track,
    };
}
function getSourceType(src) {
    const lower = src.toLowerCase();
    if (lower.includes(".mpd") || lower.includes(".dash"))
        return "dash";
    if (lower.includes(".m3u8"))
        return "hls";
    if (lower.includes(".ism/manifest") || lower.includes(".xml"))
        return "smooth";
    if (lower.endsWith(".mp4"))
        return "progressive";
    return null;
}
class Bitmovin extends AbstractBaseEngine_1.AbstractBaseEngine {
    constructor(videoElement, instanceSettings) {
        var _a, _b;
        super(videoElement, instanceSettings);
        this.name = "Bitmovin";
        this.playertechVersion = "";
        this.bitmovinPlayer =
            instanceSettings.initOptions.mediaContainer &&
                new bitmovin_player_1.Player(instanceSettings.initOptions.mediaContainer, {
                    key: instanceSettings.initOptions.bitmovinPlayerKey ||
                        process.env.BITMOVIN_KEY ||
                        "",
                    ui: false,
                    // autoplay is handled on our side to guarantee a proper eventflow
                    playback: { autoplay: false },
                });
        (_a = this.bitmovinPlayer) === null || _a === void 0 ? void 0 : _a.setVideoElement(videoElement);
        this.playertechVersion = ((_b = this.bitmovinPlayer) === null || _b === void 0 ? void 0 : _b.version) || "";
        this.setupEventListeners();
    }
    setupEventListeners() {
        if (!this.bitmovinPlayer)
            return;
        super.setupEventListeners();
        this.bitmovinPlayer.on(bitmovin_player_1.PlayerEvent.CueEnter, (event) => {
            const { vtt, text } = event;
            this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, [
                Object.assign(Object.assign({}, vtt), { text }),
            ]);
        });
        this.bitmovinPlayer.on(bitmovin_player_1.PlayerEvent.CueExit, () => {
            this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, []);
        });
    }
    setQualityLevel(level) {
        var _a, _b, _c, _d;
        if (level.id === js_player_shared_1.AUTO_QUALITY_LEVEL_DEFINITION.id) {
            (_a = this.bitmovinPlayer) === null || _a === void 0 ? void 0 : _a.setVideoQuality("auto");
        }
        else {
            const newTrackId = ((_c = (_b = this.bitmovinPlayer) === null || _b === void 0 ? void 0 : _b.getAvailableVideoQualities().find((item) => item.height === level.height)) === null || _c === void 0 ? void 0 : _c.id) || "auto";
            (_d = this.bitmovinPlayer) === null || _d === void 0 ? void 0 : _d.setVideoQuality(newTrackId);
        }
    }
    getQualityLevel() {
        var _a, _b, _c, _d;
        const tracks = (_a = this.bitmovinPlayer) === null || _a === void 0 ? void 0 : _a.getAvailableVideoQualities();
        const activeTrack = (_b = this.bitmovinPlayer) === null || _b === void 0 ? void 0 : _b.getVideoQuality();
        const activeTrackIndex = tracks === null || tracks === void 0 ? void 0 : tracks.findIndex((item) => item.id === (activeTrack === null || activeTrack === void 0 ? void 0 : activeTrack.id));
        if (!activeTrack ||
            activeTrackIndex === null ||
            activeTrackIndex === undefined ||
            activeTrackIndex < 0 ||
            activeTrack.id === "auto") {
            return super.getQualityLevel();
        }
        return {
            id: activeTrackIndex,
            bandwidth: activeTrack.bitrate,
            width: (_c = activeTrack.width) !== null && _c !== void 0 ? _c : -1,
            height: (_d = activeTrack.height) !== null && _d !== void 0 ? _d : -1,
        };
    }
    getQualityLevels() {
        var _a, _b;
        if ((_a = this.instanceSettings.loadOptions) === null || _a === void 0 ? void 0 : _a.audioOnly)
            return [];
        const tracks = (_b = this.bitmovinPlayer) === null || _b === void 0 ? void 0 : _b.getAvailableVideoQualities();
        if (!tracks || !Array.isArray(tracks))
            return [];
        const qualityLevels = tracks.map((bitrateInfo, index) => {
            const qualityLevel = {
                id: index,
                bandwidth: bitrateInfo.bitrate,
                width: bitrateInfo.width,
                height: bitrateInfo.height,
            };
            qualityLevel.name = (0, AbstractBaseEngine_1.getQualityLevelName)(qualityLevel);
            return qualityLevel;
        });
        return [...super.getQualityLevels(), ...qualityLevels];
    }
    setAudioTrack(track) {
        if (!this.bitmovinPlayer) {
            return;
        }
        this.activeAudioTrack = track;
        if (track) {
            this.bitmovinPlayer.setAudio(track.raw.id);
            this.onAudioTrackChange();
        }
    }
    getAudioTrack() {
        if (!this.bitmovinPlayer) {
            return undefined;
        }
        return this.activeAudioTrack;
    }
    getAudioTracks() {
        var _a;
        if (!this.bitmovinPlayer) {
            return [];
        }
        const tracks = this.bitmovinPlayer.getAvailableAudio();
        return (_a = tracks.map((track, index) => createAudioTrack(track, index))) !== null && _a !== void 0 ? _a : [];
    }
    setSubtitleTrack(track, shouldUpdatePreferences) {
        var _a;
        if (!this.bitmovinPlayer) {
            return;
        }
        if (!track) {
            this.bitmovinPlayer.subtitles.disable((_a = this.activeSubtitlesTrack) === null || _a === void 0 ? void 0 : _a.raw.id);
        }
        else {
            this.bitmovinPlayer.subtitles.enable(track === null || track === void 0 ? void 0 : track.raw.id);
        }
        this.activeSubtitlesTrack = track;
        this.onTextTracksChange(shouldUpdatePreferences);
    }
    getSubtitleTrack() {
        if (!this.bitmovinPlayer) {
            return undefined;
        }
        return this.activeSubtitlesTrack;
    }
    getSubtitleTracks() {
        var _a;
        if (!this.bitmovinPlayer) {
            return [];
        }
        const tracks = this.bitmovinPlayer.subtitles.list();
        return this.filterVisibleSubtitleTracks(((_a = tracks.map((track, index) => createSubtitleTrack(track, index))) !== null && _a !== void 0 ? _a : []).filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index));
    }
    _load({ src, license, startTime, subtitle, audio, }) {
        const _super = Object.create(null, {
            load: { get: () => super.load }
        });
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.bitmovinPlayer)
                return;
            const type = getSourceType(src);
            if (!type) {
                console.error(`[BitmovinEngine] Unknown source type for URL: ${src}`);
                throw new Error("Unsupported source type");
            }
            const config = {
                [type]: startTime ? `${src}#t=${startTime}` : src,
                drm: {
                    widevine: {
                        LA_URL: (_a = license === null || license === void 0 ? void 0 : license["com.widevine.alpha"]) === null || _a === void 0 ? void 0 : _a.licenseServerUrl,
                    },
                    fairplay: {
                        LA_URL: (_b = license === null || license === void 0 ? void 0 : license["com.apple.fps"]) === null || _b === void 0 ? void 0 : _b.licenseServerUrl,
                        certificateURL: (_c = license === null || license === void 0 ? void 0 : license["com.apple.fps"]) === null || _c === void 0 ? void 0 : _c.certificateUrl,
                    },
                    playready: {
                        LA_URL: (_d = license === null || license === void 0 ? void 0 : license["com.microsoft.playready"]) === null || _d === void 0 ? void 0 : _d.licenseServerUrl,
                    },
                },
            };
            if (config.drm &&
                config.drm.fairplay &&
                ((_e = license === null || license === void 0 ? void 0 : license["com.apple.fps"]) === null || _e === void 0 ? void 0 : _e.licenseServerUrl) &&
                ((_f = license === null || license === void 0 ? void 0 : license["com.apple.fps"]) === null || _f === void 0 ? void 0 : _f.licenseServerUrl.includes("drmtoday")) &&
                ((_g = this.instanceSettings.playResponse) === null || _g === void 0 ? void 0 : _g.playToken)) {
                config.drm.fairplay.headers = {
                    "x-dt-auth-token": (_h = this.instanceSettings.playResponse) === null || _h === void 0 ? void 0 : _h.playToken,
                };
                config.drm.fairplay.prepareContentId = (contentId) => {
                    // initData contains [4 byte: Length][EXT-X-KEY URI] but since Bitmovin converts it to string and
                    // each char is 2 bytes, we need to skip the first 2 chars of contentId
                    const skdUrl = new URL(contentId.substring(2));
                    return skdUrl.hostname;
                };
                config.drm.fairplay.prepareMessage = (event, session) => {
                    return new URLSearchParams({
                        spc: event.messageBase64Encoded,
                        contentId: session.contentId,
                    });
                };
                config.drm.fairplay.prepareLicense = (license) => {
                    return new Uint8Array(license);
                };
                config.drm.fairplay.licenseResponseType = bitmovin_player_1.HttpResponseType.ARRAYBUFFER;
            }
            if (config.drm &&
                config.drm.widevine &&
                ((_j = license === null || license === void 0 ? void 0 : license["com.widevine.alpha"]) === null || _j === void 0 ? void 0 : _j.licenseServerUrl) &&
                ((_k = license === null || license === void 0 ? void 0 : license["com.widevine.alpha"]) === null || _k === void 0 ? void 0 : _k.licenseServerUrl.includes("drmtoday")) &&
                ((_l = this.instanceSettings.playResponse) === null || _l === void 0 ? void 0 : _l.playToken)) {
                config.drm.widevine.headers = {
                    "x-dt-auth-token": (_m = this.instanceSettings.playResponse) === null || _m === void 0 ? void 0 : _m.playToken,
                };
                config.drm.widevine.prepareLicense = (licenseResponse) => {
                    const license = { license: licenseResponse.license };
                    try {
                        const drmTodayObj = JSON.parse(String.fromCharCode.apply(null, licenseResponse.license));
                        if (drmTodayObj && drmTodayObj.status && drmTodayObj.license) {
                            if (drmTodayObj.status === "OK") {
                                const str = window.atob(drmTodayObj.license);
                                const bufView = new Uint8Array(new ArrayBuffer(str.length));
                                for (let i = 0; i < str.length; i++) {
                                    bufView[i] = str.charCodeAt(i);
                                }
                                license.license = bufView;
                            }
                            else {
                                // license not okay
                            }
                        }
                        else {
                            // no valid DRMtoday license
                        }
                    }
                    catch (e) {
                        // no valid DRMtoday license
                    }
                    return license;
                };
            }
            this.bitmovinPlayer.load(config).then(() => _super.load.call(this, {
                src,
                license,
                startTime,
                audio,
                subtitle,
            }));
        });
    }
    seekTo(pos) {
        var _a;
        // bitmovin can't seek to by setting this.videoElement.currentTime property.
        (_a = this.bitmovinPlayer) === null || _a === void 0 ? void 0 : _a.seek(pos);
    }
    load(parameters) {
        this._load(parameters);
    }
    destroy() {
        var _a;
        (_a = this.bitmovinPlayer) === null || _a === void 0 ? void 0 : _a.destroy();
        super.destroy();
    }
}
exports.Bitmovin = Bitmovin;
//# sourceMappingURL=Bitmovin.js.map